import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Typography,
  Box,
  Alert,
  List,
  ListItemButton,
  ListItemText,
  Radio,
  Divider,
  Chip,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import intl from 'components/i18n/ReactIntlWrapper';
import { LedgerMovement } from 'store/registry/useLedgerMovements';

interface Candidate {
  id: number;
  amount: number;       // cents
  incoming: number;
  date: string;
  description: string;
  account?: { id: number; name: string; accountNumber: string };
  currency?: { code: string };
}

interface Props {
  open: boolean;
  movement: LedgerMovement | null;
  onConfirmLink: (targetSeatId: number) => Promise<void>;
  onConfirmUnlink: () => Promise<void>;
  onCancel: () => void;
}

const LedgerLinkDialog: React.FC<Props> = ({
  open,
  movement,
  onConfirmLink,
  onConfirmUnlink,
  onCancel,
}) => {
  const api: Api = useApi();

  const isAlreadyLinked = movement?.movementType === 'linked' && !!movement?.idRelatedAccountingSeat;

  const [term, setTerm] = useState('');
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setTerm('');
      setCandidates([]);
      setSelectedId(null);
      setError(null);
    }
  }, [open]);

  const fetchCandidates = useCallback(
    async (searchTerm: string) => {
      if (!movement) return;
      setLoading(true);
      setError(null);
      try {
        const res = await api.invoiceApiWrapper.getLedgerLinkCandidates('1', movement.id, searchTerm);
        setCandidates((res.data as any)?.data ?? []);
      } catch {
        setError(intl.get('ledgerLink.errorLoadingCandidates') || 'Failed to load candidates.');
        setCandidates([]);
      } finally {
        setLoading(false);
      }
    },
    [api, movement]
  );

  // Debounced search
  useEffect(() => {
    if (!open || isAlreadyLinked) return;
    const timer = setTimeout(() => fetchCandidates(term), 350);
    return () => clearTimeout(timer);
  }, [open, term, fetchCandidates, isAlreadyLinked]);

  const handleConfirmLink = async () => {
    if (selectedId === null) return;
    setSubmitting(true);
    try {
      await onConfirmLink(selectedId);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmUnlink = async () => {
    setSubmitting(true);
    try {
      await onConfirmUnlink();
    } finally {
      setSubmitting(false);
    }
  };

  const formatAmount = (cents: number, currencyCode = '') =>
    `${(cents / 100).toFixed(2)} ${currencyCode}`.trim();

  const directionLabel = (incoming: number) =>
    incoming === 1
      ? intl.get('ledgerLink.credit') || 'Credit'
      : intl.get('ledgerLink.debit') || 'Debit';

  if (!movement) return null;

  // ── Already linked — show info + unlink option ────────────────────────────
  if (isAlreadyLinked) {
    return (
      <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Icon icon="mdi:link-variant" width={20} />
            {intl.get('ledgerLink.linkedTitle') || 'Linked Entry'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Alert severity="info">
              {intl.get('ledgerLink.linkedSubtitle') ||
                'This entry is linked to another movement across accounts.'}
            </Alert>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {intl.get('ledgerLink.linkedTo') || 'Linked to entry #'}
                {movement.idRelatedAccountingSeat}
                {movement.relatedAccount && (
                  <> &mdash; {movement.relatedAccount.accountName} ({movement.relatedAccount.accountNumber})</>
                )}
              </Typography>
            </Box>
            <Alert severity="warning">
              {intl.get('ledgerLink.unlinkWarning') ||
                'Removing this link does not delete either entry. Both will become independent movements again.'}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={submitting} color="inherit">
            {intl.get('button.cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleConfirmUnlink}
            disabled={submitting}
            variant="outlined"
            color="error"
            startIcon={submitting ? <CircularProgress size={16} /> : <Icon icon="mdi:link-variant-off" width={18} />}
          >
            {intl.get('ledgerLink.unlink') || 'Remove Link'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Search for a candidate to link to ────────────────────────────────────
  const oppositeLabel = movement.type === 'debit'
    ? intl.get('ledgerLink.credit') || 'credit'
    : intl.get('ledgerLink.debit') || 'debit';

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Icon icon="mdi:link-variant-plus" width={20} />
          {intl.get('ledgerLink.title') || 'Link to Another Entry'}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {/* Source movement summary */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block">
              {intl.get('ledgerLink.source') || 'Source entry'}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              <Chip
                label={directionLabel(movement.incoming)}
                size="small"
                color={movement.type === 'credit' ? 'success' : 'error'}
              />
              <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                {formatAmount(movement.amount * 100, movement.currency?.code)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date(movement.date).toLocaleDateString()}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" noWrap>
              {movement.description}
            </Typography>
          </Box>

          <Alert severity="info">
            {intl.get('ledgerLink.instruction') ||
              `Select a ${oppositeLabel} entry from another account to link with this one.`}
          </Alert>

          {/* Search field */}
          <TextField
            label={intl.get('ledgerLink.searchPlaceholder') || 'Search by description or account name'}
            size="small"
            fullWidth
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            InputProps={{
              endAdornment: loading ? <CircularProgress size={16} /> : null,
            }}
          />

          {error && <Alert severity="error">{error}</Alert>}

          {/* Candidate list */}
          {!loading && candidates.length === 0 && term.length >= 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={1}>
              {intl.get('ledgerLink.noCandidates') || 'No matching entries found.'}
            </Typography>
          )}

          {candidates.length > 0 && (
            <List disablePadding sx={{ maxHeight: 280, overflow: 'auto' }}>
              {candidates.map((c) => (
                <React.Fragment key={c.id}>
                  <ListItemButton
                    selected={selectedId === c.id}
                    onClick={() => setSelectedId(c.id)}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <Radio checked={selectedId === c.id} size="small" sx={{ mr: 1 }} />
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Chip
                            label={directionLabel(c.incoming)}
                            size="small"
                            color={c.incoming === 1 ? 'success' : 'error'}
                          />
                          <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                            {formatAmount(c.amount, c.currency?.code)}
                          </Typography>
                          <Chip
                            label={new Date(c.date).toLocaleDateString()}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" display="block">
                            {c.account?.name ?? ''}{c.account?.accountNumber ? ` — ${c.account.accountNumber}` : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c.description}
                          </Typography>
                        </>
                      }
                    />
                  </ListItemButton>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting} color="inherit">
          {intl.get('button.cancel') || 'Cancel'}
        </Button>
        <Button
          onClick={handleConfirmLink}
          disabled={submitting || selectedId === null}
          variant="contained"
          color="primary"
        >
          {submitting ? (
            <CircularProgress size={18} sx={{ color: '#fff' }} />
          ) : (
            intl.get('ledgerLink.confirm') || 'Link Entries'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LedgerLinkDialog;
